import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, KeyRound, Dumbbell, Salad, ChevronDown, ChevronUp, Bell, BellOff, BookOpen, ExternalLink, Ruler, Trash2, Calendar as CalendarIcon } from 'lucide-react';
import CalendarView from '../components/CalendarView';
import axios from 'axios';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const Area = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(() => {
    try { return JSON.parse(localStorage.getItem('student_user')) || null; } catch { return null; }
  });
  const [tab, setTab] = useState('workout');
  const [workout, setWorkout] = useState(null);
  const [diet, setDiet] = useState(null);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMeal, setOpenMeal] = useState(null);
  const [pushStatus, setPushStatus] = useState('idle'); // 'idle'|'subscribed'|'denied'|'loading'
  const [studentProfile, setStudentProfile] = useState(null);
  const [workoutAssignments, setWorkoutAssignments] = useState([]);
  const [dietAssignments, setDietAssignments] = useState([]);
  const [trackingData, setTrackingData] = useState({}); // keyed by `${assignmentId}-${exerciseIndex}`

  // Self-measurement state
  const [showMeasForm, setShowMeasForm] = useState(false);
  const [measForm, setMeasForm] = useState({ weight: '', height: '', bodyFat: '', muscleMass: '', waist: '', hip: '', notes: '', date: '' });
  const [measError, setMeasError] = useState('');
  const [measSaving, setMeasSaving] = useState(false);

  // ── Service Worker + Push setup (auto-subscribe) ─────────────────────────────
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').catch(() => {});

    if (Notification.permission === 'denied') {
      setPushStatus('denied');
      return;
    }

    // Já tem permissão: verifica se já está inscrito
    if (Notification.permission === 'granted') {
      navigator.serviceWorker.ready.then(async reg => {
        const sub = await reg.pushManager.getSubscription();
        if (sub) { setPushStatus('subscribed'); return; }
        // Permissão concedida mas sem subscription — reinscreve automaticamente
        autoSubscribe(reg);
      });
      return;
    }

    // Primeira vez: pede permissão automaticamente após pequeno delay
    const timer = setTimeout(() => autoSubscribe(), 1500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line
  }, []);

  const autoSubscribe = async (reg) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/push/vapid-key`);
      if (!data.publicKey) return;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushStatus(perm === 'denied' ? 'denied' : 'idle'); return; }
      const sw = reg || await navigator.serviceWorker.ready;
      const sub = await sw.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: data.publicKey });
      const token = localStorage.getItem('student_token');
      await axios.post(`${BACKEND_URL}/api/push/subscribe`, { subscription: sub.toJSON() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPushStatus('subscribed');
    } catch { setPushStatus('idle'); }
  };

  const handlePushToggle = async () => {
    const token = localStorage.getItem('student_token');
    if (pushStatus === 'subscribed') {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        await axios.delete(`${BACKEND_URL}/api/push/subscribe`, {
          data: { endpoint: sub.endpoint },
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
      setPushStatus('idle');
      return;
    }

    setPushStatus('loading');
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/push/vapid-key`);
      if (!data.publicKey) { setPushStatus('idle'); return; }

      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushStatus('denied'); return; }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: data.publicKey,
      });
      await axios.post(`${BACKEND_URL}/api/push/subscribe`, { subscription: sub.toJSON() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPushStatus('subscribed');
    } catch { setPushStatus('idle'); }
  };

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) { navigate('/login'); return; }

    Promise.all([
      axios.get(`${BACKEND_URL}/api/students/me`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${BACKEND_URL}/api/students/workout`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${BACKEND_URL}/api/students/diet`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${BACKEND_URL}/api/contents`),
      axios.get(`${BACKEND_URL}/api/students/profile`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${BACKEND_URL}/api/students/workout-assignments`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/students/diet-assignments`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
    ])
      .then(([meRes, wRes, dRes, contentsRes, profileRes, waRes, daRes]) => {
        setStudent(meRes.data);
        localStorage.setItem('student_user', JSON.stringify(meRes.data));
        setWorkout(wRes.data);
        setDiet(dRes.data);
        setContents(contentsRes.data || []);
        setStudentProfile(profileRes.data);
        setWorkoutAssignments(waRes.data || []);
        setDietAssignments(daRes.data || []);
      })
      .catch(() => {
        localStorage.removeItem('student_token');
        localStorage.removeItem('student_user');
        navigate('/login');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-white" />
      </div>
    );
  }

  const totalCals = diet?.meals?.reduce((sum, m) => sum + m.foods.reduce((s, f) => s + (Number(f.calories) || 0), 0), 0) || 0;
  const totalProt = diet?.meals?.reduce((sum, m) => sum + m.foods.reduce((s, f) => s + (Number(f.protein) || 0), 0), 0) || 0;

  const renderContents = () => (
    <>
      {contents.length === 0 ? (
        <div className="text-center py-20 text-[#555]">
          <BookOpen size={40} className="mx-auto mb-4 opacity-20" />
          <p>Nenhum conteúdo disponível ainda.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {contents.map((c, i) => (
            <a key={i} href={c.url} target="_blank" rel="noopener noreferrer"
              className="bg-[#121212] border border-white/5 hover:border-white/20 px-5 py-4 flex items-center justify-between gap-4 transition-colors"
            >
              <div className="min-w-0">
                <p className="font-medium text-sm">{c.title}</p>
                {c.description && <p className="text-xs text-[#555] mt-0.5">{c.description}</p>}
              </div>
              <ExternalLink size={14} className="text-[#555] flex-shrink-0" />
            </a>
          ))}
        </div>
      )}
    </>
  );

  const renderMeasurements = () => {
    const canSelfMeasure = student?.permissions?.measurements;

    const handleMeasSubmit = async (e) => {
      e.preventDefault();
      setMeasSaving(true);
      setMeasError('');
      try {
        const token = localStorage.getItem('student_token');
        const res = await axios.post(`${BACKEND_URL}/api/students/profile/measurements`, measForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudentProfile(res.data);
        setMeasForm({ weight: '', height: '', bodyFat: '', muscleMass: '', waist: '', hip: '', notes: '', date: '' });
        setShowMeasForm(false);
      } catch (err) {
        const data = err.response?.data;
        if (data?.nextAllowed) {
          setMeasError(`Você já registrou uma medição nos últimos 30 dias. Próxima medição disponível em: ${data.nextAllowed}`);
        } else {
          setMeasError(data?.detail || 'Erro ao registrar medição.');
        }
      } finally { setMeasSaving(false); }
    };

    const handleDeleteMeasurement = async (idx) => {
      if (!window.confirm('Remover esta medição?')) return;
      try {
        const token = localStorage.getItem('student_token');
        const res = await axios.delete(`${BACKEND_URL}/api/students/profile/measurements/${idx}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudentProfile(res.data);
      } catch (err) {
        alert(err.response?.data?.detail || 'Erro ao remover medição.');
      }
    };

    const inputMeasCls = 'bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none px-3 py-2 text-sm w-full transition-colors';
    const labelMeasCls = 'block text-xs text-[#555] uppercase tracking-widest mb-1';

    return (
      <>
        {canSelfMeasure && (
          <div className="mb-6">
            <button onClick={() => { setShowMeasForm(v => !v); setMeasError(''); }}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors"
            >
              <Ruler size={14} /> {showMeasForm ? 'Cancelar' : 'Registrar minhas medidas'}
            </button>

            {showMeasForm && (
              <form onSubmit={handleMeasSubmit} className="mt-4 bg-[#121212] border border-white/5 p-5 flex flex-col gap-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className={labelMeasCls}>Peso (kg)</label>
                    <input type="number" step="0.1" className={inputMeasCls} placeholder="70.5"
                      value={measForm.weight} onChange={e => setMeasForm(f => ({ ...f, weight: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelMeasCls}>Altura (cm)</label>
                    <input type="number" step="0.1" className={inputMeasCls} placeholder="175"
                      value={measForm.height} onChange={e => setMeasForm(f => ({ ...f, height: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelMeasCls}>% Gordura</label>
                    <input type="number" step="0.1" className={inputMeasCls} placeholder="18.5"
                      value={measForm.bodyFat} onChange={e => setMeasForm(f => ({ ...f, bodyFat: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelMeasCls}>M. Muscular (kg)</label>
                    <input type="number" step="0.1" className={inputMeasCls} placeholder="35"
                      value={measForm.muscleMass} onChange={e => setMeasForm(f => ({ ...f, muscleMass: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelMeasCls}>Cintura (cm)</label>
                    <input type="number" step="0.1" className={inputMeasCls} placeholder="82"
                      value={measForm.waist} onChange={e => setMeasForm(f => ({ ...f, waist: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelMeasCls}>Quadril (cm)</label>
                    <input type="number" step="0.1" className={inputMeasCls} placeholder="98"
                      value={measForm.hip} onChange={e => setMeasForm(f => ({ ...f, hip: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelMeasCls}>Data</label>
                    <input type="date" className={inputMeasCls}
                      value={measForm.date} onChange={e => setMeasForm(f => ({ ...f, date: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelMeasCls}>Obs.</label>
                    <input type="text" className={inputMeasCls} placeholder="Opcional"
                      value={measForm.notes} onChange={e => setMeasForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>
                {measError && <p className="text-red-400 text-sm">{measError}</p>}
                <button type="submit" disabled={measSaving}
                  className="self-start px-5 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50">
                  {measSaving ? 'Salvando...' : 'Registrar'}
                </button>
              </form>
            )}
          </div>
        )}

        {!studentProfile?.measurements?.length ? (
          <div className="text-center py-20 text-[#555]">
            <Ruler size={40} className="mx-auto mb-4 opacity-20" />
            <p>Nenhuma medição registrada ainda.</p>
            {!canSelfMeasure && <p className="text-xs mt-1">Seu treinador irá inserir suas medidas após a consulta.</p>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-3 text-xs text-[#555] uppercase tracking-widest font-medium">Data</th>
                  <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Peso</th>
                  <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Altura</th>
                  <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">% Gord.</th>
                  <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">M. Musc.</th>
                  <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Cintura</th>
                  <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Quadril</th>
                  <th className="py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Obs.</th>
                  <th className="py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Origem</th>
                  {canSelfMeasure && <th className="py-2 pl-2 w-8"></th>}
                </tr>
              </thead>
              <tbody>
                {studentProfile.measurements.map((m, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-3 pr-3 text-[#A3A3A3]">{m.date || '—'}</td>
                    <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.weight ? `${m.weight}kg` : '—'}</td>
                    <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.height ? `${m.height}cm` : '—'}</td>
                    <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.bodyFat ? `${m.bodyFat}%` : '—'}</td>
                    <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.muscleMass ? `${m.muscleMass}kg` : '—'}</td>
                    <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.waist ? `${m.waist}cm` : '—'}</td>
                    <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.hip ? `${m.hip}cm` : '—'}</td>
                    <td className="py-3 px-2 text-[#555] text-xs hidden sm:table-cell">
                      {m.notes ? <span className="max-w-[120px] truncate block" title={m.notes}>{m.notes}</span> : '—'}
                    </td>
                    <td className="py-3 px-2 text-center hidden sm:table-cell">
                      {m.addedBy === 'student'
                        ? <span className="text-xs border border-white/10 px-1.5 py-0.5 text-[#555]">Eu</span>
                        : <span className="text-xs border border-white/10 px-1.5 py-0.5 text-[#555]">Daniel</span>
                      }
                    </td>
                    {canSelfMeasure && (
                      <td className="py-3 pl-2">
                        {m.addedBy === 'student' && (
                          <button onClick={() => handleDeleteMeasurement(i)} className="text-[#555] hover:text-red-400 transition-colors">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  // Pending state — awaiting first consultation
  if (student?.status === 'pending') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold tracking-tight uppercase">Área do Aluno</h1>
              {student && <p className="text-[#A3A3A3] text-sm mt-1">Olá, <span className="text-white">{student.name}</span></p>}
            </div>
          </div>

          {/* Pending message */}
          <div className="bg-[#121212] border border-white/5 p-8 text-center mb-8">
            <div className="w-12 h-12 rounded-full border border-yellow-400/30 flex items-center justify-center mx-auto mb-4">
              <span className="text-yellow-400 text-xl">⏳</span>
            </div>
            <h2 className="text-lg font-bold mb-2">Aguardando sua primeira consulta</h2>
            <p className="text-[#A3A3A3] text-sm leading-relaxed max-w-md mx-auto">
              Sua conta foi criada com sucesso. Em breve seu plano personalizado estará disponível aqui. Fique à vontade para explorar os conteúdos abaixo enquanto aguarda.
            </p>
          </div>

          {/* Show content and measurements tabs */}
          <div className="flex border-b border-white/10 mb-8 overflow-x-auto hide-scrollbar">
            {student?.permissions?.content && (
              <button onClick={() => setTab('content')}
                className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-medium uppercase tracking-wider transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === 'content' ? 'text-white border-white' : 'text-[#555] border-transparent hover:text-[#A3A3A3]'}`}
              >
                <BookOpen size={14} /> Conteúdos
              </button>
            )}
            <button onClick={() => setTab('measurements')}
              className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-medium uppercase tracking-wider transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === 'measurements' ? 'text-white border-white' : 'text-[#555] border-transparent hover:text-[#A3A3A3]'}`}
            >
              <Ruler size={14} /> Medidas
            </button>
          </div>
          {tab === 'content' && renderContents()}
          {tab === 'measurements' && renderMeasurements()}
        </div>
      </div>
    );
  }

  const tabs = [
    student?.permissions?.workout      && ['workout',      <Dumbbell size={14} />,  'Treino'],
    student?.permissions?.diet         && ['diet',         <Salad size={14} />,     'Dieta'],
    ['measurements', <Ruler size={14} />, 'Medidas'],  // always visible
    (student?.permissions?.workout || student?.permissions?.diet) && ['calendar', <CalendarIcon size={14} />, 'Calendário'],
    student?.permissions?.content      && ['content',      <BookOpen size={14} />,  'Conteúdos'],
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase">Área do Aluno</h1>
            {student && <p className="text-[#A3A3A3] text-sm mt-1">Olá, <span className="text-white">{student.name}</span></p>}
          </div>
          <div className="flex gap-2">
            {'Notification' in window && pushStatus !== 'denied' && (
              <button onClick={handlePushToggle} disabled={pushStatus === 'loading'}
                title={pushStatus === 'subscribed' ? 'Desativar notificações' : 'Ativar notificações'}
                className={`flex items-center gap-2 px-4 py-2 border text-sm transition-colors ${pushStatus === 'subscribed' ? 'bg-[#121212] border-white/30 text-white' : 'bg-[#121212] border-white/10 hover:border-white/30 text-[#555]'}`}
              >
                {pushStatus === 'subscribed' ? <Bell size={14} /> : <BellOff size={14} />}
                <span className="hidden sm:inline">{pushStatus === 'subscribed' ? 'Notificações on' : 'Notificações'}</span>
              </button>
            )}
            <button onClick={() => navigate('/trocar-senha')}
              className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 hover:border-white/30 text-sm transition-colors"
            >
              <KeyRound size={14} />
              <span className="hidden sm:inline">Trocar senha</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-8">
          {tabs.map(([key, icon, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-medium uppercase tracking-wider transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === key ? 'text-white border-white' : 'text-[#555] border-transparent hover:text-[#A3A3A3]'}`}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        {/* ── TREINO ── */}
        {tab === 'workout' && (
          <>
            {workoutAssignments.length > 0 ? (
              <div className="flex flex-col gap-6">
                {workoutAssignments.map(assignment => {
                  const today = new Date().toISOString().slice(0, 10);
                  let badgeCls = 'text-green-400 border-green-500/30';
                  let badgeLabel = 'Ativo';
                  if (assignment.endDate && assignment.endDate < today) { badgeCls = 'text-[#555] border-white/10'; badgeLabel = 'Encerrado'; }
                  else if (assignment.startDate && assignment.startDate > today) { badgeCls = 'text-yellow-400 border-yellow-400/30'; badgeLabel = 'Próximo'; }

                  return (
                    <div key={assignment.id} className="bg-[#121212] border border-white/5 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="text-base font-bold">{assignment.name || 'Treino'}</h2>
                            <span className={`text-xs border px-2 py-0.5 ${badgeCls}`}>{badgeLabel}</span>
                          </div>
                          {(assignment.startDate || assignment.endDate) && (
                            <p className="text-xs text-[#555] mt-0.5">
                              {assignment.startDate} — {assignment.endDate || 'sem prazo'}
                            </p>
                          )}
                        </div>
                      </div>

                      {assignment.scheduledDays && assignment.scheduledDays.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'].map(d => (
                            <span key={d}
                              className={`text-xs border px-3 py-1 ${assignment.scheduledDays.includes(d) ? 'border-white/30 text-white' : 'border-white/5 text-[#555]'}`}
                            >{d}</span>
                          ))}
                        </div>
                      )}

                      {assignment.exercises && assignment.exercises.length > 0 && (
                        <div>
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b border-white/10">
                                <th className="text-left py-2 pr-4 text-xs text-[#555] uppercase tracking-widest font-medium">Exercício</th>
                                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Séries</th>
                                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Reps</th>
                                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Prescrito</th>
                                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Meu registro</th>
                              </tr>
                            </thead>
                            <tbody>
                              {assignment.exercises.map((ex, i) => {
                                const key = `${assignment.id}-${i}`;
                                const tracking = trackingData[key] || { weight: '', reps: '', series: '' };
                                return (
                                  <tr key={i} className="border-b border-white/5">
                                    <td className="py-3 pr-4">
                                      <p className="font-medium">{ex.name}</p>
                                      {ex.notes && <p className="text-xs text-[#555] mt-0.5">{ex.notes}</p>}
                                    </td>
                                    <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{ex.series || '—'}</td>
                                    <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{ex.reps || '—'}</td>
                                    <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.prescribedWeight || ex.weight || '—'}</td>
                                    <td className="py-3 px-2">
                                      <input
                                        value={tracking.weight}
                                        onChange={e => setTrackingData(d => ({ ...d, [key]: { ...tracking, weight: e.target.value } }))}
                                        className="bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none px-2 py-1 text-xs w-full transition-colors"
                                        placeholder="carga"
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <button
                            onClick={async () => {
                              const token = localStorage.getItem('student_token');
                              try {
                                await axios.patch(
                                  `${BACKEND_URL}/api/students/workout-assignments/${assignment.id}/tracking`,
                                  { tracking: trackingData },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                              } catch { alert('Erro ao salvar registro.'); }
                            }}
                            className="mt-3 px-4 py-1.5 text-xs bg-white text-black font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors"
                          >
                            Salvar registro
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : !workout ? (
              <div className="text-center py-20 text-[#555]">
                <Dumbbell size={40} className="mx-auto mb-4 opacity-20" />
                <p>Seu treino ainda não foi configurado.</p>
                <p className="text-xs mt-1">Aguarde seu nutricionista.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">{workout.name}</h2>
                  {workout.days?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {workout.days.map(d => (
                        <span key={d} className="text-xs border border-white/10 px-3 py-1 text-[#A3A3A3]">{d}</span>
                      ))}
                    </div>
                  )}
                </div>

                {workout.exercises?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 pr-4 text-xs text-[#555] uppercase tracking-widest font-medium">Exercício</th>
                          <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Séries</th>
                          <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Reps</th>
                          <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Carga</th>
                          <th className="text-center py-2 pl-2 text-xs text-[#555] uppercase tracking-widest font-medium">Descanso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workout.exercises.map((ex, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="py-3 pr-4">
                              <p className="font-medium">{ex.name}</p>
                              {ex.notes && <p className="text-xs text-[#555] mt-0.5">{ex.notes}</p>}
                            </td>
                            <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.series || '—'}</td>
                            <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.reps || '—'}</td>
                            <td className="py-3 px-2 text-center text-[#A3A3A3]">{ex.weight || '—'}</td>
                            <td className="py-3 pl-2 text-center text-[#A3A3A3]">{ex.rest || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {workout.notes && (
                  <div className="bg-[#121212] border border-white/5 p-4 text-sm text-[#A3A3A3] leading-relaxed">
                    {workout.notes}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ── DIETA ── */}
        {tab === 'diet' && (
          <>
            {dietAssignments.length > 0 ? (
              <div className="flex flex-col gap-4">
                {dietAssignments.map(assignment => {
                  const today = new Date().toISOString().slice(0, 10);
                  let badgeCls = 'text-green-400 border-green-500/30';
                  let badgeLabel = 'Ativo';
                  if (assignment.endDate && assignment.endDate < today) { badgeCls = 'text-[#555] border-white/10'; badgeLabel = 'Encerrado'; }
                  else if (assignment.startDate && assignment.startDate > today) { badgeCls = 'text-yellow-400 border-yellow-400/30'; badgeLabel = 'Próximo'; }

                  return (
                    <div key={assignment.id}>
                      <div className="flex items-center gap-2 mb-3">
                        <h2 className="text-base font-bold">{assignment.name || 'Dieta'}</h2>
                        <span className={`text-xs border px-2 py-0.5 ${badgeCls}`}>{badgeLabel}</span>
                        {(assignment.startDate || assignment.endDate) && (
                          <span className="text-xs text-[#555]">{assignment.startDate} — {assignment.endDate || 'sem prazo'}</span>
                        )}
                      </div>
                      {assignment.meals?.map((meal, mi) => {
                        const mealKey = `${assignment.id}-${mi}`;
                        return (
                          <div key={mi} className="bg-[#121212] border border-white/5 mb-2">
                            <button
                              onClick={() => setOpenMeal(openMeal === mealKey ? null : mealKey)}
                              className="w-full flex items-center justify-between px-5 py-4 text-left"
                            >
                              <div className="flex items-center gap-3">
                                <span className="font-medium">{meal.name || `Refeição ${mi + 1}`}</span>
                                {meal.time && <span className="text-xs text-[#555]">{meal.time}</span>}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-[#555]">
                                  {(meal.foods || []).reduce((s, f) => s + (Number(f.calories) || 0), 0)} kcal
                                </span>
                                {openMeal === mealKey ? <ChevronUp size={14} className="text-[#555]" /> : <ChevronDown size={14} className="text-[#555]" />}
                              </div>
                            </button>
                            {openMeal === mealKey && (
                              <div className="border-t border-white/5 px-5 pb-4">
                                <table className="w-full text-sm mt-3">
                                  <thead>
                                    <tr className="border-b border-white/5">
                                      <th className="text-left pb-2 text-xs text-[#555] font-medium">Alimento</th>
                                      <th className="text-right pb-2 text-xs text-[#555] font-medium">Qtd</th>
                                      <th className="text-right pb-2 text-xs text-[#555] font-medium">Kcal</th>
                                      <th className="text-right pb-2 text-xs text-[#555] font-medium hidden sm:table-cell">Prot</th>
                                      <th className="text-right pb-2 text-xs text-[#555] font-medium hidden sm:table-cell">Carb</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(meal.foods || []).map((f, fi) => (
                                      <tr key={fi} className="border-b border-white/5 last:border-0">
                                        <td className="py-2 text-[#E5E5E5]">
                                          {f.name}
                                          {f.alternatives && f.alternatives.length > 0 && (
                                            <span className="ml-2 text-xs text-[#555]">
                                              (ou: {f.alternatives.map(a => a.name).join(', ')})
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-2 text-right text-[#555]">{f.quantity}</td>
                                        <td className="py-2 text-right text-[#A3A3A3]">{f.calories || '—'}</td>
                                        <td className="py-2 text-right text-[#A3A3A3] hidden sm:table-cell">{f.protein ? `${f.protein}g` : '—'}</td>
                                        <td className="py-2 text-right text-[#A3A3A3] hidden sm:table-cell">{f.carbs ? `${f.carbs}g` : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ) : !diet ? (
              <div className="text-center py-20 text-[#555]">
                <Salad size={40} className="mx-auto mb-4 opacity-20" />
                <p>Sua dieta ainda não foi configurada.</p>
                <p className="text-xs mt-1">Aguarde seu nutricionista.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">{diet.name}</h2>
                  <div className="flex gap-4 text-sm text-[#A3A3A3]">
                    {diet.calories && <span><b className="text-white">{diet.calories}</b> kcal</span>}
                    {totalProt > 0 && <span><b className="text-white">{Math.round(totalProt)}g</b> prot</span>}
                  </div>
                </div>

                {diet.meals?.map((meal, mi) => (
                  <div key={mi} className="bg-[#121212] border border-white/5">
                    <button
                      onClick={() => setOpenMeal(openMeal === mi ? null : mi)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{meal.name || `Refeição ${mi + 1}`}</span>
                        {meal.time && <span className="text-xs text-[#555]">{meal.time}</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#555]">
                          {meal.foods.reduce((s, f) => s + (Number(f.calories) || 0), 0)} kcal
                        </span>
                        {openMeal === mi ? <ChevronUp size={14} className="text-[#555]" /> : <ChevronDown size={14} className="text-[#555]" />}
                      </div>
                    </button>

                    {openMeal === mi && (
                      <div className="border-t border-white/5 px-5 pb-4">
                        <table className="w-full text-sm mt-3">
                          <thead>
                            <tr className="border-b border-white/5">
                              <th className="text-left pb-2 text-xs text-[#555] font-medium">Alimento</th>
                              <th className="text-right pb-2 text-xs text-[#555] font-medium">Qtd</th>
                              <th className="text-right pb-2 text-xs text-[#555] font-medium">Kcal</th>
                              <th className="text-right pb-2 text-xs text-[#555] font-medium hidden sm:table-cell">Prot</th>
                              <th className="text-right pb-2 text-xs text-[#555] font-medium hidden sm:table-cell">Carb</th>
                            </tr>
                          </thead>
                          <tbody>
                            {meal.foods.map((f, fi) => (
                              <tr key={fi} className="border-b border-white/5 last:border-0">
                                <td className="py-2 text-[#E5E5E5]">{f.name}</td>
                                <td className="py-2 text-right text-[#555]">{f.quantity}</td>
                                <td className="py-2 text-right text-[#A3A3A3]">{f.calories || '—'}</td>
                                <td className="py-2 text-right text-[#A3A3A3] hidden sm:table-cell">{f.protein ? `${f.protein}g` : '—'}</td>
                                <td className="py-2 text-right text-[#A3A3A3] hidden sm:table-cell">{f.carbs ? `${f.carbs}g` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}

                {diet.notes && (
                  <div className="bg-[#121212] border border-white/5 p-4 text-sm text-[#A3A3A3] leading-relaxed">
                    {diet.notes}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ── CALENDÁRIO ── */}
        {tab === 'calendar' && (
          <CalendarView workoutAssignments={workoutAssignments} dietAssignments={dietAssignments} />
        )}

        {/* ── CONTEÚDOS ── */}
        {tab === 'content' && renderContents()}

        {/* ── MEDIDAS ── */}
        {tab === 'measurements' && renderMeasurements()}
      </div>
    </div>
  );
};

export default Area;
